import React, { useRef, useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import TWModal from './tw-modal';
import TWButton from './tw-button';
import { cn } from './_cn';

const tones = {
  danger: {
    icon: ExclamationTriangleIcon,
    iconWrap:
      'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
    confirmVariant: 'danger',
  },
  warning: {
    icon: ShieldExclamationIcon,
    iconWrap:
      'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    confirmVariant: 'primary',
  },
  info: {
    icon: InformationCircleIcon,
    iconWrap:
      'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    confirmVariant: 'primary',
  },
};

const TWConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  loading = false,
  requireTypedConfirmation,
  confirmDisabled = false,
  children,
}) => {
  const cancelRef = useRef(null);
  const [typed, setTyped] = useState('');
  const cfg = tones[tone] || tones.danger;
  const Icon = cfg.icon;

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const typeBlocks = Boolean(
    requireTypedConfirmation && typed !== requireTypedConfirmation,
  );

  return (
    <TWModal
      open={open}
      onClose={onClose}
      size="md"
      initialFocus={cancelRef}
      footer={
        <div className="flex flex-row-reverse gap-2">
          <TWButton
            variant={cfg.confirmVariant}
            loading={loading}
            disabled={typeBlocks || confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </TWButton>
          <TWButton
            ref={cancelRef}
            variant="default"
            disabled={loading}
            onClick={onClose}
          >
            {cancelLabel}
          </TWButton>
        </div>
      }
    >
      <div className="flex gap-4">
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            cfg.iconWrap,
          )}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          {title ? (
            // `Dialog.Title`, not a plain <h3>: TWModal renders the actual
            // Headless UI `<Dialog>` this component's content lands inside
            // (as `children`), so `Dialog.Title` registers into that same
            // component's context regardless of the nesting in between and
            // gives the dialog an `aria-labelledby` pointing here for free —
            // a plain heading gives assistive tech no accessible name at
            // all. Same visual output (`as="h3"` matches the class this
            // replaces), purely additive.
            <Dialog.Title
              as="h3"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              {title}
            </Dialog.Title>
          ) : null}
          {description ? (
            // Same reasoning as `Dialog.Title` above: registers an
            // `aria-describedby` on the dialog automatically. Default tag is
            // already `p`, so nothing else changes.
            <Dialog.Description className="text-sm text-slate-500 dark:text-slate-400">
              {description}
            </Dialog.Description>
          ) : null}
          {children}
          {requireTypedConfirmation ? (
            <div className="space-y-1 pt-1">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Type{' '}
                <strong className="font-semibold text-slate-900 dark:text-slate-100">
                  {requireTypedConfirmation}
                </strong>{' '}
                to confirm.
              </p>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-sky-400 dark:focus:ring-sky-400"
              />
              <p className="text-[0.65rem] text-slate-400">
                This action cannot be undone.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </TWModal>
  );
};

export default TWConfirmModal;
